

import React, { useState } from 'react';
import type { MyPlayerProps, PlayerPremiumItem, PremiumItemProps } from '../../types';
import { Icons, PremiumIcons } from '../../Components';
import { Button, Text, View } from '../../Components/Themed';
import { useColors } from '../../constants/useColors';
import type { ViewStyle } from 'react-native';
import { PremiumApi } from '../api';

type ALaCartePremiumProps = {
    identifier: string,
    style?:ViewStyle,
    player?:MyPlayerProps,
    premium_item:PremiumItemProps,
    player_premium_item?:PlayerPremiumItem,
    onPurchaseItem:(player_premium_item:PlayerPremiumItem) => void,
    onViewPremium:(premium_item_id?:string) => void
}

export const getIconByFeature = (pi:PremiumItemProps, color:string) => {
    switch(pi.icon_name){
        case 'order_grades': return <PremiumIcons.PremiumOrderGrades size={30} color={color}/>
        case 'advanced_market_analytics': return  <PremiumIcons.PremiumAnalytics size={30} color={color}/>
        case 'advanced_filtering': return <PremiumIcons.PremiumFilters size={30} color={color}/>
        case 'additional_earnings': return <PremiumIcons.PremiumEarnings size={30} color={color}/>
        case 'referral_rewards': return <PremiumIcons.PremiumReferral size={30} color={color}/> 
        case 'external_pricing': return <PremiumIcons.PremiumPriceComparison size={30} color={color}/>
        case 'premium_order_actions': return <PremiumIcons.PremiumOrders size={30} color={color}/>
        case 'deposit_advantages': return <PremiumIcons.PremiumDeposits size={30} color={color}/>
        case 'additional_insights': return <PremiumIcons.PremiumDashboard size={30} color={color}/>
        case 'anonymous_mode': return <PremiumIcons.PremiumAnonymous size={30} color={color}/>
        case 'premium_social': return <PremiumIcons.PremiumSocial size={30} color={color}/>
        case 'premium_apis': return <PremiumIcons.PremiumAPI size={30} color={color}/>
        case 'premium_competitions': return <PremiumIcons.PremiumComps size={30} color={color}/>
        case 'priority_support': return <PremiumIcons.PremiumSupport size={30} color={color}/>
        case 'less_ads': return <PremiumIcons.PremiumNoAds size={30} color={color}/>
        default: return <PremiumIcons.PremiumDashboard size={30} color={color}/>
    }

}

const ALaCartePremium = ({ style, player, premium_item, player_premium_item, onPurchaseItem, onViewPremium }:ALaCartePremiumProps) => {
    const Colors = useColors();
    const [ expanded, setExpanded ] = useState(false);
    const [ purchase_loading, setPurchaseLoading ] = useState(false);
    const [ selected_price, setSelectedPrice ] = useState<'day'|'month'|undefined>(undefined);
    const [ a_la_carte_disclaimer, setALaCarteDiscliamer ] = useState(false);


    const handlePurchasePremiumItem = async() => {
        if(!selected_price){ return } //Must include a selected price
        if(purchase_loading){ return }//No mashy mashy
        if(!premium_item){ return }
        if(player_premium_item){ return }//Already have this one
        setPurchaseLoading(true);
        PremiumApi.setEnvironment();
        let ppi = await PremiumApi.purchasePremiumItem(premium_item.premium_item_id, selected_price);
        if(!ppi){ 
            alert('Unable to complete purchase')
            return setPurchaseLoading(false) 
        }
        onPurchaseItem(ppi);
        setPurchaseLoading(false)
    }


    //Never render this if the player is already premium or if they are not logged in
    if(!player || player.type == 'premium' || !premium_item){ return <></> }
    if(!premium_item.a_la_carte){ return <></> }//This is not an a la carte item

    if(player_premium_item){ return <></> } //We already have this!
    return (
        <View transparent style={{ ...style }}>
            <Button float transparent style={{ padding:10, flexDirection:'row', alignItems:'center', borderBottomWidth:expanded?0:1, borderBottomRightRadius:expanded?0:8, borderBottomLeftRadius:expanded?0:8 }} onPress={() => setExpanded(!expanded)}>
                <View transparent style={{ padding:5 }}>
                    {getIconByFeature(premium_item, Colors.text.gold)}
                </View>
                <View transparent style={{ flex:1, marginLeft:10 }}>
                    <Text size={14} theme='h1'>Unlock Premium Item</Text>
                    <Text size={14} theme='description'>{premium_item.name}</Text>
                </View>
                <Icons.ChevronIcon direction={expanded?'up':'down'} color={Colors.text.h1} size={10} />
            </Button>
            {expanded ?
            <View transparent style={{ borderWidth:1, borderRadius:8, borderTopRightRadius:0, borderTopLeftRadius:0, borderColor:Colors.borders.light }}>
                <Text style={{ padding:10 }} theme='description'>{premium_item.description}</Text>
                {selected_price ?
                <View transparent style={{ padding:10, borderRadius:4 }}>
                    <View style={{ flexDirection:'row', marginTop:5 }}>
                        <Button 
                            style={{ flex:1, marginRight:2 }} onPress={() => {
                                setSelectedPrice(undefined)
                                setALaCarteDiscliamer(false);
                            }}
                            type='error'
                            title='CANCEL'
                        />
                        <Button 
                            type='success'
                            disabled={!a_la_carte_disclaimer || purchase_loading?true:false}
                            loading={purchase_loading} 
                            title={`Confirm ${selected_price == 'day' ? premium_item.daily_price:premium_item.monthly_price}`}
                            style={{ flex:2, opacity:!a_la_carte_disclaimer||purchase_loading?0.5:1, marginLeft:2, marginRight:2 }} 
                            onPress={() => handlePurchasePremiumItem()}>
                        </Button>
                    </View>
                    <Button style={{ flexDirection:'row', marginTop:10 }} onPress={() => setALaCarteDiscliamer(!a_la_carte_disclaimer)}>
                        <View style={{ marginRight:10, height:20, width:20, borderRadius:4, borderWidth:1, borderColor:Colors.text.action, justifyContent:'center', alignItems:'center' }}>
                            {a_la_carte_disclaimer ?
                            <View style={{ height:12, width:12, backgroundColor:Colors.text.action }} />
                            :<></>}
                        </View>
                        <Text size={14} theme='description'>I understand this feature will be unlocked for the next {selected_price==='day'?'24 hours':'month'} and ${selected_price == 'day' ? premium_item.daily_price : premium_item.monthly_price} will be deducted from my wallet immediately.</Text>
                    </Button>
                </View>
                :
                <View transparent style={{ padding:10, borderRadius:4 }}>
                    <Text size={14} weight='bold' textAlign='left'>Unlock Options</Text>
                    <View transparent style={{ flexDirection:'row', marginTop:5 }}>
                        <Button 
                            float
                            style={{ flex:1, borderRadius:8, padding:10, marginRight:2 }} onPress={() => onViewPremium(premium_item.premium_item_id)}>
                            <Text size={12} textAlign='center' weight='semibold' color={Colors.text.action}>VIEW PREMIUM</Text>
                        </Button>
                        <Button
                            type='success'
                            title={`${premium_item.daily_price} - 24 Hours`}
                            style={{ flex:1, borderRadius:8, padding:10, marginLeft:2, marginRight:2, alignItems:'center' }} onPress={() => setSelectedPrice('day')}
                        />
                        <Button
                            type='success'
                            title={`${premium_item.monthly_price} - 1 Month`}
                            style={{ flex:1, borderRadius:8, padding:10, marginLeft:2, justifyContent:'center' }} onPress={() => setSelectedPrice('month')}
                        />
                    </View>
                </View>
                }
            </View>
            :<></>}
        </View>
    )
}

export default ALaCartePremium